// Used so the invoice PDF only prints complimentary text when the package form actually has it.
export const getFilledComplementaryService = (complementaryService) => {
  if (typeof complementaryService !== "string") {
    return "";
  }

  return complementaryService.trim();
};

const pad2 = (value) => String(value).padStart(2, "0");

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof value?.toDate === "function") {
    const parsed = value.toDate();
    return parsed instanceof Date && !Number.isNaN(parsed.getTime())
      ? parsed
      : null;
  }
  if (typeof value === "object" && typeof value.seconds === "number") {
    return new Date(value.seconds * 1000);
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    const dmy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
    if (dmy) {
      const [, day, month, year] = dmy;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }
    const parsed = new Date(trimmed);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof value === "number") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

export const formatInvoiceWhatsAppDate = (value) => {
  const date = toDate(value);
  if (!date) return "";
  return `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()}`;
};

export const toE164Phone = (phoneNumber, countryCode) => {
  const digits = String(phoneNumber || "").replace(/\D/g, "");
  if (!digits) return "";

  let cc = "91";
  if (countryCode && typeof countryCode === "object") {
    cc = String(countryCode.value || "").replace(/\D/g, "") || "91";
  } else if (countryCode != null && String(countryCode).trim()) {
    cc = String(countryCode).replace(/\D/g, "") || "91";
  }

  if (digits.startsWith(cc) && digits.length > cc.length) {
    return `+${digits}`;
  }
  if (digits.startsWith("0")) {
    return `+${cc}${digits.replace(/^0+/, "")}`;
  }
  return `+${cc}${digits}`;
};

export const invoiceWhatsAppPackageName = (invoice) => {
  const name =
    invoice?.serviceName || invoice?.productName || invoice?.packageName || "";
  return String(name).trim();
};

export const invoiceWhatsAppAmountPaid = (invoice) => {
  const amount =
    invoice?.amountPaid ?? invoice?.totalAmount ?? invoice?.amount ?? "";
  if (amount === "" || amount == null) return "";
  const numeric = Number(amount);
  if (Number.isFinite(numeric)) {
    return String(Number(numeric.toFixed(2)));
  }
  return String(amount).replace(/[₹,\s]/g, "").trim();
};

const filled = (value) => String(value || "").trim();

export const missingInvoiceWhatsAppFields = (invoice, rowData = {}) => {
  const bill = { ...rowData, ...invoice };
  const missing = [];
  if (!filled(bill.clientId)) missing.push("clientId");
  if (!/^https:\/\//i.test(filled(bill.invoice))) missing.push("invoice PDF");
  if (!filled(bill.invoiceId)) missing.push("invoice_number");
  if (!filled(bill.name)) missing.push("name");
  if (!invoiceWhatsAppPackageName(bill)) missing.push("package_name");
  if (!formatInvoiceWhatsAppDate(bill.startDate || bill.commencementDate)) {
    missing.push("start_date");
  }
  if (!formatInvoiceWhatsAppDate(bill.completionDate || bill.endDate)) {
    missing.push("end_date");
  }
  if (!invoiceWhatsAppAmountPaid(bill)) missing.push("amount_paid");
  return missing;
};

export const canSendInvoiceWhatsApp = (invoice, rowData = {}) => {
  return missingInvoiceWhatsAppFields(invoice, rowData).length === 0;
};

export const buildInvoiceWhatsAppPayload = (invoice, rowData = {}) => {
  const bill = { ...rowData, ...invoice };
  const missing = missingInvoiceWhatsAppFields(bill);
  if (missing.length > 0) {
    throw new Error(
      `This invoice is missing: ${missing.join(", ")}. WhatsApp send needs a package with commencement and completion dates plus a stored PDF.`
    );
  }

  const invoiceId = filled(bill.invoiceId);
  return {
    clientId: filled(bill.clientId),
    invoiceUrl: filled(bill.invoice),
    invoiceId,
    packageName: invoiceWhatsAppPackageName(bill),
    startDate: formatInvoiceWhatsAppDate(bill.startDate || bill.commencementDate),
    endDate: formatInvoiceWhatsAppDate(bill.completionDate || bill.endDate),
    amountPaid: invoiceWhatsAppAmountPaid(bill),
    filename: `${invoiceId}.pdf`,
  };
};
